import { buildUnitReferenceIndexWithRemap } from './manualUnitIdentity'
import { getLedgerSafeOrderedUnits } from './ledgerSafeBoundaryReconcile'
import type { AnchoredRow, UnitMapping } from '../types/anchoredRowTypes'
import type { CompleteMicroSegmentationResult } from '../types/segmentationTypes'

export type LedgerSafeSequenceOffenderKind = 'misplaced' | 'duplicate'

export interface LedgerSafeSequenceOffender {
  kind: LedgerSafeSequenceOffenderKind
  rowId: string
  unitIndex: number
  unitUuid: string
  unitLabel?: string
  text: string
  /** Row of the nearest in-order placement that precedes this unit in source order. */
  belongsAfterRowId: string | null
  /** Row of the nearest in-order placement that follows this unit in source order. */
  belongsBeforeRowId: string | null
  /** First row inside the correct interval without a mapped unit for this witness. */
  landingRowId: string | null
  /** For duplicates: the row that keeps the unit. */
  keptRowId?: string
}

export interface LedgerSafeSequenceStaleMapping {
  rowId: string
  unitUuid: string
  textSnapshot: string
}

export interface LedgerSafeSequenceUnplacedUnit {
  unitIndex: number
  unitUuid: string
  unitLabel?: string
  text: string
  /** Row of the nearest in-order placement that precedes this unit in source order. */
  belongsAfterRowId: string | null
  /** Row of the nearest in-order placement that follows this unit in source order. */
  belongsBeforeRowId: string | null
  /** First row inside the correct interval without a mapped unit for this witness. */
  landingRowId: string | null
}

export interface LedgerSafeSequenceAudit {
  witnessId: string
  totalUnits: number
  placedCount: number
  inOrderCount: number
  unplacedCount: number
  unplacedUnits: LedgerSafeSequenceUnplacedUnit[]
  offenders: LedgerSafeSequenceOffender[]
  staleMappings: LedgerSafeSequenceStaleMapping[]
  markedTranspositionRowIds: string[]
  offenderRowIds: Set<string>
}

interface SequencePlacement {
  rowId: string
  rowIndex: number
  unitIndex: number
  unitUuid: string
  unitLabel?: string
  text: string
}

const sortRows = (rows: AnchoredRow[]): AnchoredRow[] => [...rows].sort((left, right) => {
  if (left.position !== right.position) {
    return left.position - right.position
  }
  if (left.labelNumber !== right.labelNumber) {
    return left.labelNumber - right.labelNumber
  }
  return left.id.localeCompare(right.id)
})

const getActiveMapping = (row: AnchoredRow, witnessId: string): UnitMapping | undefined =>
  row.unitMappings.find(mapping => mapping.witnessId === witnessId && !mapping.excluded)

/**
 * Positions (into `placements`) of one longest strictly increasing subsequence
 * of unit indexes. Everything outside it is the minimal set of placements whose
 * removal restores source order.
 */
const findInOrderPositions = (placements: SequencePlacement[]): Set<number> => {
  const tails: number[] = []
  const tailPositions: number[] = []
  const parents: Array<number | null> = new Array(placements.length).fill(null)

  placements.forEach((placement, position) => {
    let low = 0
    let high = tails.length
    while (low < high) {
      const middle = (low + high) >> 1
      if (tails[middle] < placement.unitIndex) {
        low = middle + 1
      } else {
        high = middle
      }
    }
    tails[low] = placement.unitIndex
    tailPositions[low] = position
    parents[position] = low > 0 ? tailPositions[low - 1] : null
  })

  const kept = new Set<number>()
  let cursor: number | null = tailPositions.length > 0 ? tailPositions[tailPositions.length - 1] : null
  while (cursor !== null) {
    kept.add(cursor)
    cursor = parents[cursor]
  }
  return kept
}

export const buildLedgerSafeSequenceAudit = ({
  rows,
  witnessId,
  segmentationResult
}: {
  rows: AnchoredRow[]
  witnessId: string
  segmentationResult: CompleteMicroSegmentationResult | null | undefined
}): LedgerSafeSequenceAudit => {
  const sortedRows = sortRows(rows)
  const orderedUnits = getLedgerSafeOrderedUnits(segmentationResult)
  const unitLookup = buildUnitReferenceIndexWithRemap({
    units: orderedUnits.map(entry => entry.unit),
    idRemap: segmentationResult?.id_remap
  })

  const placements: SequencePlacement[] = []
  const staleMappings: LedgerSafeSequenceStaleMapping[] = []
  const markedTranspositionRowIds: string[] = []
  const duplicateOffenders: LedgerSafeSequenceOffender[] = []
  const rowIdByClaimedUnitIndex = new Map<number, string>()

  sortedRows.forEach((row, rowIndex) => {
    const mapping = getActiveMapping(row, witnessId)
    if (!mapping) {
      return
    }
    const reference = String(mapping.unitUuid || '').trim()
    const unitIndex = reference ? unitLookup.get(reference) : undefined
    if (typeof unitIndex !== 'number') {
      staleMappings.push({
        rowId: row.id,
        unitUuid: reference,
        textSnapshot: String(mapping.textSnapshot || '')
      })
      return
    }
    const unit = orderedUnits[unitIndex]?.unit
    const text = String(unit?.unit_text_content || mapping.textSnapshot || '')
    const keptRowId = rowIdByClaimedUnitIndex.get(unitIndex)
    if (keptRowId) {
      duplicateOffenders.push({
        kind: 'duplicate',
        rowId: row.id,
        unitIndex,
        unitUuid: reference,
        unitLabel: unit?.unit_label || mapping.unitLabel,
        text,
        belongsAfterRowId: null,
        belongsBeforeRowId: null,
        landingRowId: null,
        keptRowId
      })
      return
    }
    rowIdByClaimedUnitIndex.set(unitIndex, row.id)
    if (mapping.translationStatus === 'transposed') {
      // A marked transposition is a documented deviation from source order;
      // it must neither count as an offender nor constrain its neighbours.
      markedTranspositionRowIds.push(row.id)
      return
    }
    placements.push({
      rowId: row.id,
      rowIndex,
      unitIndex,
      unitUuid: reference,
      unitLabel: unit?.unit_label || mapping.unitLabel,
      text
    })
  })

  const keptPositions = findInOrderPositions(placements)
  const keptPlacements = placements.filter((_, position) => keptPositions.has(position))
  const rowIndexById = new Map(sortedRows.map((row, index) => [row.id, index]))

  const findLandingRowId = (afterRowId: string | null, beforeRowId: string | null): string | null => {
    const startIndex = afterRowId ? (rowIndexById.get(afterRowId) ?? -1) + 1 : 0
    const endIndex = beforeRowId ? (rowIndexById.get(beforeRowId) ?? sortedRows.length) : sortedRows.length
    for (let index = startIndex; index < endIndex; index += 1) {
      const candidate = sortedRows[index]
      if (!getActiveMapping(candidate, witnessId)) {
        return candidate.id
      }
    }
    return null
  }

  const findSourceOrderLocus = (unitIndex: number): {
    belongsAfterRowId: string | null
    belongsBeforeRowId: string | null
  } => {
    let belongsAfterRowId: string | null = null
    let belongsBeforeRowId: string | null = null
    let bestLowerIndex = Number.NEGATIVE_INFINITY
    let bestUpperIndex = Number.POSITIVE_INFINITY
    keptPlacements.forEach(kept => {
      if (kept.unitIndex < unitIndex && kept.unitIndex > bestLowerIndex) {
        bestLowerIndex = kept.unitIndex
        belongsAfterRowId = kept.rowId
      }
      if (kept.unitIndex > unitIndex && kept.unitIndex < bestUpperIndex) {
        bestUpperIndex = kept.unitIndex
        belongsBeforeRowId = kept.rowId
      }
    })
    return { belongsAfterRowId, belongsBeforeRowId }
  }

  const misplacedOffenders: LedgerSafeSequenceOffender[] = placements
    .filter((_, position) => !keptPositions.has(position))
    .map(placement => {
      const { belongsAfterRowId, belongsBeforeRowId } = findSourceOrderLocus(placement.unitIndex)
      return {
        kind: 'misplaced' as const,
        rowId: placement.rowId,
        unitIndex: placement.unitIndex,
        unitUuid: placement.unitUuid,
        unitLabel: placement.unitLabel,
        text: placement.text,
        belongsAfterRowId,
        belongsBeforeRowId,
        landingRowId: findLandingRowId(belongsAfterRowId, belongsBeforeRowId)
      }
    })

  const unplacedUnits: LedgerSafeSequenceUnplacedUnit[] = orderedUnits
    .map((entry, unitIndex) => ({ entry, unitIndex }))
    .filter(({ unitIndex }) => !rowIdByClaimedUnitIndex.has(unitIndex))
    .map(({ entry, unitIndex }) => {
      const { belongsAfterRowId, belongsBeforeRowId } = findSourceOrderLocus(unitIndex)
      return {
        unitIndex,
        unitUuid: String(entry.unit.unit_uuid || ''),
        unitLabel: entry.unit.unit_label,
        text: String(entry.unit.unit_text_content || ''),
        belongsAfterRowId,
        belongsBeforeRowId,
        landingRowId: findLandingRowId(belongsAfterRowId, belongsBeforeRowId)
      }
    })

  const offenders = [...misplacedOffenders, ...duplicateOffenders].sort((left, right) => {
    const leftRowIndex = rowIndexById.get(left.rowId) ?? 0
    const rightRowIndex = rowIndexById.get(right.rowId) ?? 0
    return leftRowIndex - rightRowIndex
  })

  const placedCount = placements.length + markedTranspositionRowIds.length + duplicateOffenders.length
  return {
    witnessId,
    totalUnits: orderedUnits.length,
    placedCount,
    inOrderCount: keptPlacements.length,
    unplacedCount: unplacedUnits.length,
    unplacedUnits,
    offenders,
    staleMappings,
    markedTranspositionRowIds,
    offenderRowIds: new Set(offenders.map(offender => offender.rowId))
  }
}
